import { Button } from '@music-vine/cadence/ui';
import { Dialog, DialogDescription, DialogTitle, DialogTrigger } from '@music-vine/cadence/ui';
import { Input } from '@music-vine/cadence/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { type CreateBoardMode, useBoardsContext } from './context-provider';
import { DialogContentBoards, DialogHeaderBoards } from './dialog-partials';
import { useCreateBoardHandler } from './hooks/useCreateBoardHandler';
import { getDefaultBoardName } from './utils/get-default-board-name';
interface DialogCreateBoardProps {
  children?: React.ReactNode;
  mode?: CreateBoardMode;
  onOpenChange?: (open: boolean) => void;
}
export const DialogCreateBoard = ({
  onOpenChange,
  mode = 'create_board_with_asset',
  children
}: DialogCreateBoardProps) => {
  const [inputValue, setInputValue] = useState('');
  const {
    asset,
    openNewBoardDialog,
    closeNewBoardDialog,
    newBoardDialogIsOpen
  } = useBoardsContext();
  const {
    handleCreateBoard,
    isCreatingBoard
  } = useCreateBoardHandler({
    newBoardName: inputValue.trim(),
    mode,
    onCreateBoardEnd: () => {
      closeNewBoardDialog();
      setInputValue('');
    }
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const defaultBoardName = useMemo(() => {
    if (asset) {
      return getDefaultBoardName({
        asset
      });
    }
    return 'New board';
  }, [asset]);
  const handleOnOpenChange = useCallback((open: boolean) => {
    if (open) {
      openNewBoardDialog();
    }
    if (!open) {
      closeNewBoardDialog();
      setInputValue('');
    }
    if (onOpenChange) {
      onOpenChange(open);
    }
  }, [onOpenChange, openNewBoardDialog, closeNewBoardDialog]);
  return <Dialog onOpenChange={handleOnOpenChange} open={newBoardDialogIsOpen} data-sentry-element="Dialog" data-sentry-component="DialogCreateBoard" data-sentry-source-file="dialog-create-board.tsx">
      <DialogTitle className="sr-only" data-sentry-element="DialogTitle" data-sentry-source-file="dialog-create-board.tsx">New board dialog</DialogTitle>
      <DialogDescription className="sr-only" data-sentry-element="DialogDescription" data-sentry-source-file="dialog-create-board.tsx">
        Create a new board by entering a name for your board and clicking
        create.
      </DialogDescription>
      <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="dialog-create-board.tsx">{children}</DialogTrigger>
      <DialogContentBoards onOpenAutoFocus={e => {
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.focus();
        if (asset) {
          setInputValue(defaultBoardName);
        }
      }
    }} data-sentry-element="DialogContentBoards" data-sentry-source-file="dialog-create-board.tsx">
        <div className="flex flex-col gap-4">
          <DialogHeaderBoards data-sentry-element="DialogHeaderBoards" data-sentry-source-file="dialog-create-board.tsx">Create new board</DialogHeaderBoards>
          <Input disabled={isCreatingBoard} maxLength={100} onChange={value => setInputValue(value.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleCreateBoard();
          }
        }} ref={inputRef} value={inputValue} data-sentry-element="Input" data-sentry-source-file="dialog-create-board.tsx" />
          <div className="mt-1 flex w-full justify-end gap-4">
            <Button border className="font-semibold dark:bg-gray-900 dark:hover:bg-gray-950" onClick={closeNewBoardDialog} size="lg" variant="subtle" data-sentry-element="Button" data-sentry-source-file="dialog-create-board.tsx">
              Cancel
            </Button>
            <Button className="font-semibold" disabled={isCreatingBoard || !inputValue.trim()} onClick={handleCreateBoard} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="dialog-create-board.tsx">
              Create
            </Button>
          </div>
        </div>
      </DialogContentBoards>
    </Dialog>;
};